import React, { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

import { API_URL } from '../utils/apiConfig';

const FiscalYearContext = createContext();

export const useFiscalYear = () => {
  const context = useContext(FiscalYearContext);
  if (!context) {
    throw new Error('useFiscalYear must be used within a FiscalYearProvider');
  }
  return context;
};

export const FiscalYearProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [currentFiscalYear, setCurrentFiscalYear] = useState(null);
  const [loading, setLoading] = useState(true);
  // ENH-05 2026-06-25 : scenarios partagés au context pour lookup live FY.scenario_id
  // → scenarios[id].name (remplace lecture dénormalisée fy.scenario_name). Fetch
  // initial + refetchScenarios exposé pour callers qui mutent (POST/PUT scenarios).
  const [scenarios, setScenarios] = useState([]);

  const fetchFiscalYears = async () => {
    try {
      const [yearsRes, currentRes] = await Promise.all([
        axios.get(`${API_URL}/api/fiscal-years`),
        axios.get(`${API_URL}/api/fiscal-years/current`)
      ]);
      setFiscalYears(yearsRes.data || []);
      setCurrentFiscalYear(currentRes.data);
    } catch (error) {
      logger.error('Failed to fetch fiscal years:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScenarios = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scenarios`);
      setScenarios(res.data || []);
    } catch (error) {
      logger.error('Failed to fetch scenarios:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchFiscalYears();
      fetchScenarios();
    } else {
      setFiscalYears([]);
      setCurrentFiscalYear(null);
      setScenarios([]);
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Map id→scenario pour lookup O(1) côté consumers (FiscalYearSelector,
  // FiscalYearCard, useFiscalYearsPage). Recalcul mémorisé sur scenarios.
  const scenariosById = useMemo(
    () => new Map((scenarios || []).map(s => [s.id, s])),
    [scenarios]
  );

  const selectFiscalYear = (fiscalYear) => {
    setCurrentFiscalYear(fiscalYear);
  };

  // FIX 409 sentinel périmé /context (2026-07) : resynchronise le FY en mémoire
  // depuis le doc frais renvoyé par un PUT (return_document=AFTER) — sans
  // fetchFiscalYears(), qui réécraserait la sélection avec GET /current.
  // MERGE et non remplacement : les items de GET /fiscal-years portent
  // activities_count (ajouté par la route), absent de la réponse du PUT.
  // Accepte un doc PARTIEL (ex: {id, updated_at} depuis GET /context, qui ne
  // renvoie pas le doc FY complet). Bail-out si rien ne diffère (retourne prev,
  // même référence → pas de re-render) et useCallback([]) (identité stable) :
  // garde anti-boucle pour les callers mémoïsés dépendant de ce setter
  // (fetchFiscalYearContext → useEffect de GeneralInfo).
  const applyFreshFiscalYear = useCallback((freshDoc) => {
    if (!freshDoc?.id) return;
    const mergeIfChanged = (prev) => {
      const changed = Object.keys(freshDoc).some(k => prev[k] !== freshDoc[k]);
      return changed ? { ...prev, ...freshDoc } : prev;
    };
    setCurrentFiscalYear(prev =>
      prev?.id === freshDoc.id ? mergeIfChanged(prev) : prev
    );
    setFiscalYears(prev => {
      let changed = false;
      const next = prev.map(fy => {
        if (fy.id !== freshDoc.id) return fy;
        const merged = mergeIfChanged(fy);
        if (merged !== fy) changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
  }, []);

  const createFiscalYear = async (data) => {
    const response = await axios.post(`${API_URL}/api/fiscal-years`, data);
    await fetchFiscalYears();
    return response.data;
  };

  const closeFiscalYear = async (fiscalYearId) => {
    const response = await axios.post(`${API_URL}/api/fiscal-years/${fiscalYearId}/close`);
    await fetchFiscalYears();
    return response.data;
  };

  const rectifyFiscalYear = async (fiscalYearId, reason) => {
    const response = await axios.post(`${API_URL}/api/fiscal-years/${fiscalYearId}/rectify`, { reason });
    await fetchFiscalYears();
    return response.data;
  };

  const duplicateToNewYear = async (fiscalYearId, data) => {
    const response = await axios.post(`${API_URL}/api/fiscal-years/${fiscalYearId}/duplicate`, data);
    await fetchFiscalYears();
    return response.data;
  };

  const getActivitiesForDuplication = async (fiscalYearId) => {
    const response = await axios.get(`${API_URL}/api/fiscal-years/${fiscalYearId}/activities-for-duplication`);
    return response.data;
  };

  const refreshFiscalYears = () => {
    fetchFiscalYears();
  };

  return (
    <FiscalYearContext.Provider value={{
      fiscalYears,
      currentFiscalYear,
      loading,
      selectFiscalYear,
      applyFreshFiscalYear,
      createFiscalYear,
      closeFiscalYear,
      rectifyFiscalYear,
      duplicateToNewYear,
      getActivitiesForDuplication,
      refreshFiscalYears,
      // ENH-05 lookup live scenario_name
      scenariosById,
      refetchScenarios: fetchScenarios
    }}>
      {children}
    </FiscalYearContext.Provider>
  );
};
